/**
 * @file narrativeOnlySchema.ts
 *
 * A minimal, static JSON schema (+ field mapping) covering only the narrative
 * section. Used to render a narrative-only report (which has no schema-bound
 * artifact) through the standard schema-driven components, without fetching the
 * full `/schema`. The narrative prose key differs by RMS — `Text` (eForce) vs
 * `Narrative` (KAR) — mirroring the full schema mappings.
 */
import type { JSONSchema7 } from "json-schema";
import type { FieldMapping } from "./fieldMapping";

// Single source of truth for the per-RMS narrative prose key (eForce → `Text`,
// everything else → `Narrative`). Exported so other narrative-only surfaces
// (e.g. NarrativeOnlyEditor's key resolution) can't drift from this mapping.
export const narrativeKey = (rms: string | null): "Text" | "Narrative" =>
  rms === "EF" ? "Text" : "Narrative";

export const buildNarrativeOnlySchema = (rms: string | null): JSONSchema7 => ({
  type: "object",
  title: "Narrative",
  properties: {
    Narratives: {
      type: "array",
      title: "Narratives",
      items: {
        type: "object",
        properties: {
          [narrativeKey(rms)]: { type: "string", title: "Narrative" },
        },
      },
    },
  },
});

export const buildNarrativeOnlySchemaMapping = (
  rms: string | null,
): Record<string, FieldMapping> => ({
  Narratives: {
    properties: {
      [narrativeKey(rms)]: {
        widget: "NarrativeWidget",
        fullWidth: true,
        colSpan: 12,
        options: { rows: 10 },
      },
    },
  },
});
